import { useCallback, useContext } from 'react';

import { ModalContext } from '../ui/components2/ModalManager';
import useCurrentUser from './useCurrentUser';
import { isForumCertificationBlocked } from '../libs/forumCertification';

// Nom d'enregistrement dans `modalTypes` (src/index.js).
export const FORUM_CERTIFICATION_MODAL = 'ForumCertificationModal';

/**
 * Gate du forum réservé aux profils certifiés (cf. libs/forumCertification.js).
 *
 * Volontairement SÉPARÉ de `useRestriction` : ce dernier arbitre des droits attachés au palier
 * d'abonnement et conclut par une modale d'offres. Ici la réponse attendue est la CERTIFICATION,
 * qui est gratuite — l'y greffer aurait mêlé les deux discours et fait courir un risque de
 * régression aux règles existantes. Même raisonnement, et même forme, que `usePublicationCurfew`.
 *
 * @returns {{ isBlocked: boolean, openForumCertificationModal: function, guard: function }}
 */
const useForumCertification = () => {
    const currentUser = useCurrentUser();
    const { openModal } = useContext(ModalContext);

    // Verdict servi par l'API dans `currentUser` : pas de recalcul local de la règle.
    const isBlocked = isForumCertificationBlocked(currentUser);

    const openForumCertificationModal = useCallback(() => {
        openModal(FORUM_CERTIFICATION_MODAL);
    }, [openModal]);

    /**
     * Enveloppe le handler d'un CTA du forum : sans certification, on ouvre la modale au lieu
     * d'exécuter l'action. Le membre reste libre de recliquer — la modale se rouvre à chaque fois,
     * c'est justement l'effet d'incitation recherché.
     */
    const guard = useCallback((handler) => (...args) => {
        if (isBlocked) {
            const [event] = args;
            if (typeof event?.preventDefault === 'function') event.preventDefault();
            openForumCertificationModal();
            return undefined;
        }
        return handler?.(...args);
    }, [isBlocked, openForumCertificationModal]);

    return { isBlocked, openForumCertificationModal, guard };
};

export default useForumCertification;
